import { Component, type ReactNode } from "react";
import { RefreshCw, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { reportComponentError } from "@/lib/errorReporter";

interface Props {
  children: ReactNode;
}

interface State {
  hasChunkError: boolean;
  hasRenderError: boolean;
  error: Error | null;
}

const RELOAD_KEY = "chunk_reload_attempted";
const RELOAD_TTL_MS = 10_000;

function isChunkLoadError(error: Error): boolean {
  return (
    error.name === "ChunkLoadError" ||
    error.message.includes("Loading chunk") ||
    error.message.includes("dynamically imported module") ||
    error.message.includes("Failed to fetch dynamically imported module") ||
    error.message.includes("Importing a module script failed")
  );
}

export class ChunkErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasChunkError: false, hasRenderError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    if (isChunkLoadError(error)) {
      return { hasChunkError: true, error };
    }
    return { hasRenderError: true, error };
  }

  componentDidCatch(error: Error) {
    if (!isChunkLoadError(error)) {
      reportComponentError(error, "ChunkErrorBoundary");
      return;
    }

    const lastAttempt = sessionStorage.getItem(RELOAD_KEY);
    const now = Date.now();

    if (lastAttempt && now - Number(lastAttempt) < RELOAD_TTL_MS) {
      reportComponentError(error, "ChunkErrorBoundary");
      return;
    }

    sessionStorage.setItem(RELOAD_KEY, String(now));
    window.location.reload();
  }

  handleManualReload = () => {
    sessionStorage.removeItem(RELOAD_KEY);
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  render() {
    if (this.state.hasChunkError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4 p-8 text-center">
          <RefreshCw className="h-10 w-10 text-muted-foreground" />
          <h2 className="text-lg font-semibold">A new version is available</h2>
          <p className="text-muted-foreground max-w-md">
            The app has been updated. Please refresh to load the latest version.
          </p>
          <Button onClick={this.handleManualReload} data-testid="button-chunk-reload">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh Page
          </Button>
        </div>
      );
    }

    if (this.state.hasRenderError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4 p-8 text-center">
          <AlertTriangle className="h-10 w-10 text-destructive" />
          <h2 className="text-lg font-semibold">Something went wrong</h2>
          <p className="text-muted-foreground max-w-md">
            This page encountered an unexpected error. Please try refreshing or go back to the home page.
          </p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={this.handleGoHome} data-testid="button-error-home">
              Go Home
            </Button>
            <Button onClick={this.handleManualReload} data-testid="button-error-reload">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh Page
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
